import { sanityClient, sanityOriginClient } from './client';
import type {
  ArticlePageAccessLevel,
  SanityArticleCategory,
  SanityArticleDocumentType,
  SanityArticleFeaturedImage,
  SanityAuthor
} from './types';

/**
 * Comfortably more than one contributor writes in a season. Listings read the
 * whole scope in one query while volumes are this small; paginate when a
 * category outgrows it.
 */
export const LISTING_LIMIT = 100;

export interface SanityListingCard {
  _id: string;
  _type: SanityArticleDocumentType;
  title: string;
  slug: string;
  legacySlug?: string;
  standfirst?: string;
  publishedAt?: string;
  _createdAt: string;
  accessLevel: ArticlePageAccessLevel;
  /** Length of the body text, for an estimated read time; never the text itself. */
  bodyCharacterCount?: number;
  category?: SanityArticleCategory | null;
  author?: Pick<SanityAuthor, 'name' | 'slug' | 'avatar'> | null;
  featuredImage?: SanityArticleFeaturedImage;
}

export interface SanityArticleCategoryPage extends SanityArticleCategory {
  description?: string;
}

// Card fields only. Never project body or premiumBody themselves: a listing
// payload is public and would otherwise carry every article on the page. Only
// their text length leaves Sanity, for the read time.
export const LISTING_CARD_PROJECTION = `{
    _id,
    _type,
    title,
    "slug": slug.current,
    "legacySlug": legacySlug.current,
    standfirst,
    publishedAt,
    _createdAt,
    accessLevel,
    "bodyCharacterCount": coalesce(length(pt::text(body)), 0) + coalesce(length(pt::text(premiumBody)), 0),
    category->{ title, "slug": slug.current },
    author->{ name, "slug": slug.current, avatar { ..., asset->{ _id, url } } },
    featuredImage { ..., asset->{ _id, url, metadata { dimensions } } }
  }`;

const LISTING_ORDER = `| order(coalesce(publishedAt, _createdAt) desc) [0...$limit]`;

export const latestListingCardsQuery = `
  *[_type in ["articlePage", "teamReveal"] && defined(slug.current)] ${LISTING_ORDER} ${LISTING_CARD_PROJECTION}
`;

export const listingCardsByCategoryQuery = `
  *[_type in ["articlePage", "teamReveal"] && category->slug.current == $slug] ${LISTING_ORDER} ${LISTING_CARD_PROJECTION}
`;

export const listingCardsByAuthorQuery = `
  *[_type in ["articlePage", "teamReveal"] && author->slug.current == $slug] ${LISTING_ORDER} ${LISTING_CARD_PROJECTION}
`;

export const teamRevealCardsByAuthorQuery = `
  *[_type == "teamReveal" && author->slug.current == $slug] ${LISTING_ORDER} ${LISTING_CARD_PROJECTION}
`;

export const authorBySlugQuery = `
  *[_type == "author" && slug.current == $slug][0] {
    name,
    "slug": slug.current,
    bio,
    teamRevealBio,
    twitterHandle,
    avatar { ..., asset->{ _id, url } }
  }
`;

export const articleCategoryBySlugQuery = `
  *[_type == "articleCategory" && slug.current == $slug][0] {
    title,
    "slug": slug.current,
    description
  }
`;

// The publish webhook regenerates these listings seconds after a piece goes
// live, when the API CDN can still serve the list from before the publish. A
// regeneration from the CDN keeps the new piece off the page, or in its old
// place, until the next ISR window, so the cards read the origin.
const fetchCards = (query: string, slug: string) =>
  sanityOriginClient.fetch<SanityListingCard[]>(query, {
    slug,
    limit: LISTING_LIMIT
  });

export const getLatestListingCards = (limit: number = LISTING_LIMIT) =>
  sanityOriginClient.fetch<SanityListingCard[]>(latestListingCardsQuery, {
    limit
  });

export const getListingCardsByCategory = (slug: string) =>
  fetchCards(listingCardsByCategoryQuery, slug);

export const getListingCardsByAuthor = (slug: string) =>
  fetchCards(listingCardsByAuthorQuery, slug);

export const getTeamRevealCardsByAuthor = (slug: string) =>
  fetchCards(teamRevealCardsByAuthorQuery, slug);

/**
 * Authors worth a sitemap entry. An author document with nothing credited to
 * it renders an empty listing, which is a thin page not worth sending
 * crawlers to.
 */
export const authorSlugsWithContentQuery = `
  *[_type == "author" && defined(slug.current)
    && count(*[_type in ["articlePage", "teamReveal"] && references(^._id)]) > 0
  ] | order(slug.current asc) { "slug": slug.current }
`;

/**
 * Authors whose /team-reveals listing is worth a sitemap entry, for the same
 * reason: one with no reveals renders an empty listing.
 */
export const authorSlugsWithTeamRevealsQuery = `
  *[_type == "author" && defined(slug.current)
    && count(*[_type == "teamReveal" && references(^._id)]) > 0
  ] | order(slug.current asc) { "slug": slug.current }
`;

const fetchAuthorSlugs = async (query: string): Promise<string[]> => {
  const authors = await sanityClient.fetch<{ slug: string }[]>(query);
  return authors.map((author) => author.slug);
};

export const getAuthorSlugsWithContent = (): Promise<string[]> =>
  fetchAuthorSlugs(authorSlugsWithContentQuery);

export const getAuthorSlugsWithTeamReveals = (): Promise<string[]> =>
  fetchAuthorSlugs(authorSlugsWithTeamRevealsQuery);

export const getAuthor = async (slug: string): Promise<SanityAuthor | null> =>
  (await sanityClient.fetch<SanityAuthor | null>(authorBySlugQuery, {
    slug
  })) ?? null;

export const getArticleCategory = async (
  slug: string
): Promise<SanityArticleCategoryPage | null> =>
  (await sanityClient.fetch<SanityArticleCategoryPage | null>(
    articleCategoryBySlugQuery,
    { slug }
  )) ?? null;
